import { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import InstructorCalendar from './InstructorCalendar';
import '../../components/layout/Layout.css';

const BASE_NAV = [
  { path: '/instructor', label: 'Dashboard', icon: '▦', exact: true },
  { path: '/instructor/profile', label: 'My Profile', icon: '👤' },
  { path: '/instructor/availability', label: 'Availability', icon: '🗓' },
  { path: '/instructor/sessions', label: 'Sessions', icon: '📋' },
  { path: '/instructor/calendar', label: 'Calendar', icon: '📆' },
  { path: '/instructor/support', label: 'Support', icon: '🎫' },
];

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const SPECIALTIES_OPTIONS = ['Hitting','Pitching','Fielding','Catching','Base Running','Mental Game'];

const formatDate = d => {
  if (!d) return '-';
  const parts = d.split('T')[0].split('-');
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
};

const formatDateShort = d => {
  if (!d) return '-';
  const parts = d.split('T')[0].split('-');
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
};

const formatTime = t => {
  if (!t) return '';
  const [h,m] = t.split(':');
  const hour = parseInt(h);
  return `${hour>12?hour-12:hour||12}:${m} ${hour>=12?'PM':'AM'}`;
};

const STATUS_COLORS = { confirmed:'badge-success', pending:'badge-warning', cancelled:'badge-error', completed:'badge-info', no_show:'badge-default' };

const INSTR_STATUS_ACTIONS = {
  pending:   [{ label:'Confirm',  status:'confirmed', style:'badge-success', message:'Confirm this session?' },
              { label:'Cancel',   status:'cancelled', style:'badge-error',   message:'Cancel this session? This cannot be undone.' }],
  confirmed: [{ label:'No Show',  status:'no_show',   style:'badge-default', message:'Mark this session as a no show?' },
              { label:'Cancel',   status:'cancelled', style:'badge-error',   message:'Cancel this session? This cannot be undone.' }],
  completed: [], cancelled: [], no_show: [],
};

// ---- Confirm Modal ----
function ConfirmModal({ action, session, onConfirm, onClose, updating }) {
  const [reason, setReason] = useState('');
  const isCancelling = action.status === 'cancelled';
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><span className="modal-title">CONFIRM ACTION</span><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <p style={{ color:'var(--text-secondary)', fontSize:'0.95rem', lineHeight:'1.6' }}>{action.message}</p>
          <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'12px 16px', marginTop:'4px' }}>
            <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'6px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase' }}>Session Details</div>
            <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:'500' }}>{session.parent_first} {session.parent_last}</div>
            <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{formatDate(session.session_date)} · {formatTime(session.start_time)} – {formatTime(session.end_time)}</div>
          </div>
          {isCancelling && (
            <div className="form-group" style={{ marginTop:'4px' }}>
              <label className="form-label">Reason for cancellation (optional)</label>
              <textarea className="form-input" rows={3} placeholder="e.g. Schedule conflict, weather, etc." value={reason} onChange={e => setReason(e.target.value)} style={{ resize:'vertical' }} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={updating}>No, Go Back</button>
          <button className={`btn btn-sm ${isCancelling ? '' : 'btn-primary'}`}
            style={isCancelling ? { background:'var(--error)', color:'#fff' } : {}}
            onClick={() => onConfirm(reason)} disabled={updating}>
            {updating ? 'Updating...' : `Yes, ${action.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Session Detail Modal ----
function SessionDetailModal({ session, onClose, onUpdated }) {
  const [note, setNote] = useState(session.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const saveNote = async () => {
    setSaving(true);
    try {
      await axios.patch(`/api/instructor/sessions/${session.id}/note`, { note });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:'520px' }}>
        <div className="modal-header"><span className="modal-title">SESSION DETAILS</span><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'4px' }}>
            {[
              ['Date', formatDate(session.session_date)],
              ['Time', `${formatTime(session.start_time)} – ${formatTime(session.end_time)}`],
              ['Parent', `${session.parent_first} ${session.parent_last}`],
              ['Phone', session.parent_phone || '—'],
              ['Email', session.parent_email || '—'],
              ['Player', session.player_first ? `${session.player_first} ${session.player_last}` : '—'],
              ['Duration', `${session.duration_minutes} min`],
              ['Status', session.status],
            ].map(([label, value]) => (
              <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>{label}</div>
                <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:'500', textTransform: label === 'Status' ? 'capitalize' : 'none' }}>{value}</div>
              </div>
            ))}
          </div>
          {session.cancellation_reason && (
            <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
              <div style={{ fontSize:'0.7rem', color:'var(--error)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>Cancellation Reason</div>
              <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{session.cancellation_reason}</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Session Notes</label>
            <textarea className="form-input" rows={3} placeholder="Add notes about this session..." value={note} onChange={e => setNote(e.target.value)} style={{ resize:'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={saving}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Dashboard Home ----
function InstructorHome({ supportBadge }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/instructor/stats')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const maxDay = data ? Math.max(...data.busyDays.map(d => parseInt(d.count) || 0), 1) : 1;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">MY DASHBOARD</h1><p className="page-subtitle">Your schedule and performance at a glance</p></div>

      {supportBadge > 0 && (
        <div onClick={() => navigate('/instructor/support')} style={{
          display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px',
          background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.25)',
          borderRadius:'var(--radius-md)', marginBottom:'16px', cursor:'pointer', transition:'background 0.15s',
        }}
          onMouseEnter={e => e.currentTarget.style.background='rgba(96,165,250,0.14)'}
          onMouseLeave={e => e.currentTarget.style.background='rgba(96,165,250,0.08)'}>
          <span style={{ fontSize:'1.1rem' }}>🎫</span>
          <div style={{ flex:1 }}>
            <span style={{ fontWeight:'600', fontSize:'0.875rem', color:'#60a5fa' }}>
              {supportBadge} new support {supportBadge === 1 ? 'reply' : 'replies'}
            </span>
            <span style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginLeft:'8px' }}>— click to view</span>
          </div>
          <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>→</span>
        </div>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading...</p> : !data ? null : (
        <>
          {/* Pending Sessions Alert */}
          {parseInt(data.stats.pending || 0) > 0 && (
            <div style={{ background:'rgba(250,204,21,0.08)', border:'1px solid rgba(250,204,21,0.3)', borderRadius:'var(--radius-lg)', padding:'16px 20px', marginBottom:'24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <span style={{ fontSize:'1.3rem' }}>⚠</span>
                <div>
                  <div style={{ fontWeight:'600', color:'#facc15', fontSize:'0.95rem' }}>
                    {data.stats.pending} Pending Session{parseInt(data.stats.pending) > 1 ? 's' : ''} Awaiting Confirmation
                  </div>
                  <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginTop:'2px' }}>
                    Parents are waiting — please confirm or cancel these sessions.
                  </div>
                </div>
              </div>
              <button onClick={() => window.location.href='/instructor/sessions'} className="btn btn-sm" style={{ background:'#facc15', color:'#0a0a0a', fontWeight:'700', whiteSpace:'nowrap' }}>
                Review Now
              </button>
            </div>
          )}

          {/* KPI Stats */}
          <div className="stats-grid" style={{ marginBottom:'24px' }}>
            <div className="stat-card"><div className="stat-card-label">Today</div><div className="stat-card-value">{data.stats.today}</div></div>
            <div className="stat-card"><div className="stat-card-label">Upcoming (7 days)</div><div className="stat-card-value">{data.stats.upcoming}</div></div>
            <div className="stat-card"><div className="stat-card-label">Completion Rate</div><div className="stat-card-value" style={{fontSize:'1.6rem',color:'#4ade80'}}>{data.stats.completion_rate ?? 0}%</div></div>
            <div className="stat-card"><div className="stat-card-label">Cancellation Rate</div><div className="stat-card-value" style={{fontSize:'1.6rem',color:'#f87171'}}>{data.stats.cancellation_rate ?? 0}%</div></div>
            <div className="stat-card"><div className="stat-card-label">Total Sessions</div><div className="stat-card-value">{data.stats.total}</div></div>
            <div className="stat-card"><div className="stat-card-label">No Shows</div><div className="stat-card-value">{data.stats.no_shows}</div></div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'24px' }}>
            {/* Busiest Days */}
            <div className="card">
              <div className="stat-card-label" style={{ marginBottom:'16px' }}>Busiest Days</div>
              {data.busyDays.length === 0 ? (
                <p style={{ color:'var(--text-muted)', fontSize:'0.85rem' }}>No data yet</p>
              ) : data.busyDays.map((d, i) => (
                <div key={i} style={{ marginBottom:'10px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                    <span style={{ fontSize:'0.8rem', color:'var(--text-secondary)' }}>{d.day}</span>
                    <span style={{ fontSize:'0.8rem', fontWeight:'600', color:'var(--text-primary)' }}>{d.count}</span>
                  </div>
                  <div style={{ height:'6px', background:'var(--bg-elevated)', borderRadius:'3px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${(parseInt(d.count)/maxDay)*100}%`, background:'#fff', borderRadius:'3px', transition:'width 600ms ease' }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Session breakdown */}
            <div className="card">
              <div className="stat-card-label" style={{ marginBottom:'16px' }}>Session Breakdown</div>
              {[
                { label:'Completed', value: data.stats.completed, color:'#4ade80' },
                { label:'Confirmed', value: data.stats.confirmed || 0, color:'#60a5fa' },
                { label:'Pending',   value: data.stats.pending || 0,   color:'#facc15' },
                { label:'Cancelled', value: data.stats.cancelled,       color:'#f87171' },
                { label:'No Shows',  value: data.stats.no_shows,        color:'#a0a0a0' },
              ].map((item, i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:item.color }} />
                    <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{item.label}</span>
                  </div>
                  <span style={{ fontSize:'0.9rem', fontWeight:'600', color:item.color }}>{item.value ?? 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* This week's sessions */}
          <div className="table-container">
            <div className="table-toolbar"><span className="table-toolbar-title">Next 7 Days</span></div>
            {data.weekSessions.length === 0 ? (
              <div className="empty-state"><span className="empty-state-icon">📅</span><span className="empty-state-text">No sessions in the next 7 days</span></div>
            ) : (
              <table>
                <thead><tr><th>Date</th><th>Time</th><th>Parent</th><th>Player</th><th>Status</th></tr></thead>
                <tbody>
                  {data.weekSessions.map(s => (
                    <tr key={s.id}>
                      <td className="td-primary">{formatDateShort(s.session_date)}</td>
                      <td>{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                      <td>{s.parent_first} {s.parent_last}</td>
                      <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                      <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---- Profile ----
function InstructorProfile() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password:'', new_password:'', confirm_password:'' });
  const [pwError, setPwError] = useState('');
  const [pwSaved, setPwSaved] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const fetchProfile = async () => {
    try {
      const r = await axios.get('/api/instructor/profile');
      setProfile(r.data.profile);
      setForm(r.data.profile);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const toggleSpecialty = s => setForm(f => ({
    ...f,
    specialties: (f.specialties||[]).includes(s) ? f.specialties.filter(x => x !== s) : [...(f.specialties||[]), s]
  }));

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError(''); setSaved(false);
    try {
      await axios.patch('/api/instructor/profile', {
        bio: form.bio,
        specialties: form.specialties,
        session_duration_minutes: parseInt(form.session_duration_minutes),
        offers_30_min: form.offers_30_min !== false,
        offers_60_min: form.offers_60_min !== false,
        max_advance_booking_days: parseInt(form.max_advance_booking_days),
        phone: form.phone,
        email_notifications: form.email_notifications,
        sms_notifications: form.sms_notifications,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await fetchProfile();
    } catch (err) { setError(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async e => {
    e.preventDefault(); setPwError(''); setPwSaving(true);
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError('Passwords do not match.'); setPwSaving(false); return; }
    try {
      await axios.post('/api/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      logout().finally(() => navigate('/login'));
    } catch (err) { setPwError(err.response?.data?.error || 'Failed to change password.'); }
    finally { setPwSaving(false); }
  };

  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setPhotoError('Only JPG, PNG, and WebP images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('Image must be under 5MB.');
      return;
    }
    setPhotoUploading(true); setPhotoError(''); setPhotoSuccess(false);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      await axios.post('/api/instructor/profile/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotoSuccess(true);
      setTimeout(() => setPhotoSuccess(false), 3000);
      await fetchProfile();
    } catch (err) {
      setPhotoError(err.response?.data?.error || 'Upload failed.');
    } finally { setPhotoUploading(false); }
  };

  if (!form) return <div className="page"><p style={{ color:'var(--text-muted)' }}>Loading...</p></div>;

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">MY PROFILE</h1><p className="page-subtitle">Manage your instructor profile and preferences</p></div>

      {/* Photo Upload */}
      <div className="card" style={{ marginBottom:'20px' }}>
        <div className="stat-card-label" style={{ marginBottom:'16px' }}>Profile Photo</div>
        <div style={{ display:'flex', alignItems:'center', gap:'20px', flexWrap:'wrap' }}>
          <div style={{ width:'96px', height:'96px', borderRadius:'50%', background:'var(--bg-elevated)', border:'2px solid var(--border-light)', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {profile && profile.photo_url ? (
              <img src={profile.photo_url + '?t=' + Date.now()} alt="Profile" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            ) : (
              <span style={{ fontSize:'2rem', color:'var(--text-muted)', fontWeight:'700' }}>{form.first_name?.[0]}{form.last_name?.[0]}</span>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            <p style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>Upload a professional photo. JPG, PNG or WebP, max 5MB.</p>
            <label style={{ cursor:'pointer' }}>
              <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handlePhotoUpload} disabled={photoUploading} />
              <span className={`btn btn-outline btn-sm ${photoUploading ? 'disabled' : ''}`}>
                {photoUploading ? 'Uploading...' : form.photo_url ? 'Change Photo' : 'Upload Photo'}
              </span>
            </label>
            {photoError && <p style={{ fontSize:'0.8rem', color:'var(--error)' }}>{photoError}</p>}
            {photoSuccess && <p style={{ fontSize:'0.8rem', color:'#4ade80' }}>✓ Photo updated successfully</p>}
          </div>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>

          {/* Personal Info */}
          <div className="card">
            <div className="stat-card-label" style={{ marginBottom:'16px' }}>Personal Information</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="form-input" value={form.first_name || ''} disabled style={{ opacity:0.5 }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="form-input" value={form.last_name || ''} disabled style={{ opacity:0.5 }} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" value={form.email || ''} disabled style={{ opacity:0.5 }} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input name="phone" className="form-input" value={form.phone || ''} onChange={handleChange} placeholder="+1 (555) 000-0000" />
              </div>
            </div>
          </div>

          {/* Booking Settings */}
          <div className="card">
            <div className="stat-card-label" style={{ marginBottom:'16px' }}>Booking Settings</div>
            <div style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <div className="form-group">
                <label className="form-label">Session Duration</label>
                <select name="session_duration_minutes" className="form-input" value={form.session_duration_minutes || 60} onChange={handleChange}>
                  {[30,45,60,90,120].map(d => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Session Duration Options</label>
                <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'10px' }}>Which session lengths can parents choose when booking?</p>
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
                    <input type="checkbox" checked={form.offers_30_min !== false} onChange={e => setForm(f => ({ ...f, offers_30_min: e.target.checked }))} />
                    <span style={{ color:'var(--text-primary)', fontSize:'0.9rem' }}>30-minute sessions</span>
                  </label>
                  <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
                    <input type="checkbox" checked={form.offers_60_min !== false} onChange={e => setForm(f => ({ ...f, offers_60_min: e.target.checked }))} />
                    <span style={{ color:'var(--text-primary)', fontSize:'0.9rem' }}>60-minute sessions</span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Max Advance Booking (days)</label>
                <select name="max_advance_booking_days" className="form-input" value={form.max_advance_booking_days || 30} onChange={handleChange}>
                  {[7,14,21,30,60,90].map(d => <option key={d} value={d}>{d} days</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="card" style={{ marginBottom:'20px' }}>
          <div className="stat-card-label" style={{ marginBottom:'16px' }}>Bio</div>
          <textarea name="bio" className="form-input" rows={4} placeholder="Tell parents about your coaching background and experience..." value={form.bio || ''} onChange={handleChange} style={{ resize:'vertical' }} />
        </div>

        {/* Specialties */}
        <div className="card" style={{ marginBottom:'20px' }}>
          <div className="stat-card-label" style={{ marginBottom:'16px' }}>Specialties</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'10px' }}>
            {SPECIALTIES_OPTIONS.map(s => (
              <button key={s} type="button"
                onClick={() => toggleSpecialty(s)}
                className={`badge ${(form.specialties||[]).includes(s) ? 'badge-info' : 'badge-default'}`}
                style={{ cursor:'pointer', padding:'6px 14px', fontSize:'0.8rem' }}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Notifications */}
        <div className="card" style={{ marginBottom:'20px' }}>
          <div className="stat-card-label" style={{ marginBottom:'16px' }}>Notification Preferences</div>
          <div style={{ display:'flex', gap:'24px', flexWrap:'wrap', alignItems:'center' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer' }}>
              <input type="checkbox" checked={form.email_notifications ?? true}
                onChange={e => setForm(f => ({ ...f, email_notifications: e.target.checked }))}
                style={{ width:'16px', height:'16px', cursor:'pointer' }} />
              <span style={{ fontSize:'0.9rem', color:'var(--text-secondary)' }}>Email Notifications</span>
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'not-allowed', opacity:0.45 }}>
              <input type="checkbox" checked={false} disabled style={{ width:'16px', height:'16px', cursor:'not-allowed' }} />
              <span style={{ fontSize:'0.9rem', color:'var(--text-secondary)' }}>SMS Notifications</span>
            </label>
            <span style={{ fontSize:'0.78rem', color:'var(--text-muted)', fontStyle:'italic' }}>Option Returning Soon</span>
          </div>
        </div>

        {error && <div className="auth-error-banner" style={{ marginBottom:'16px' }}><span>⚠</span> {error}</div>}
        {saved && <div style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:'var(--radius-md)', padding:'10px 16px', color:'#4ade80', fontSize:'0.875rem', marginBottom:'16px' }}>✓ Profile saved successfully</div>}

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
      </form>

      {/* Change Password */}
      <div className="card" style={{ marginTop:'24px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div className="stat-card-label">Password</div>
          <button className="btn btn-outline btn-sm" onClick={() => setChangingPassword(c => !c)}>
            {changingPassword ? 'Cancel' : 'Change Password'}
          </button>
        </div>
        {pwSaved && <div style={{ background:'rgba(74,222,128,0.1)', border:'1px solid rgba(74,222,128,0.3)', borderRadius:'var(--radius-md)', padding:'10px 16px', color:'#4ade80', fontSize:'0.875rem', marginTop:'12px' }}>✓ Password changed successfully</div>}
        {changingPassword && (
          <form onSubmit={handlePasswordChange} style={{ marginTop:'16px', display:'flex', flexDirection:'column', gap:'14px' }}>
            {pwError && <div className="auth-error-banner"><span>⚠</span> {pwError}</div>}
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input type="password" className="form-input" value={pwForm.current_password} onChange={e => setPwForm(f => ({...f, current_password:e.target.value}))} required />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="form-input" value={pwForm.new_password} onChange={e => setPwForm(f => ({...f, new_password:e.target.value}))} required minLength={8} />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input type="password" className="form-input" value={pwForm.confirm_password} onChange={e => setPwForm(f => ({...f, confirm_password:e.target.value}))} required />
              </div>
            </div>
            <div>
              <button type="submit" className="btn btn-primary btn-sm" disabled={pwSaving}>{pwSaving ? 'Saving...' : 'Update Password'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---- Availability ----
function InstructorAvailability() {
  const [slots, setSlots] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [slotForm, setSlotForm] = useState({ day_of_week:1, start_time:'09:00', end_time:'17:00' });
  const [exForm, setExForm] = useState({ exception_date:'', start_time:'', end_time:'', reason:'' });
  const [slotError, setSlotError] = useState('');
  const [exError, setExError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [avail, prof] = await Promise.all([axios.get('/api/instructor/availability'), axios.get('/api/instructor/profile')]);
      setSlots(avail.data.slots); setExceptions(avail.data.exceptions); setProfile(prof.data.profile);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchData(); }, []);

  const [selectedDays, setSelectedDays] = useState([1]);

  const toggleDay = (i) => {
    setSelectedDays(days =>
      days.includes(i) ? (days.length > 1 ? days.filter(d => d !== i) : days) : [...days, i]
    );
  };

  const toggleAllDays = () => {
    setSelectedDays(selectedDays.length === 7 ? [slotForm.day_of_week] : [0,1,2,3,4,5,6]);
  };

  const saveSlot = async e => {
    e.preventDefault(); setSaving(true); setSlotError('');
    try {
      const results = await Promise.allSettled(
        selectedDays.map(day => axios.post('/api/instructor/availability/slots', { ...slotForm, day_of_week: day }))
      );
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length === results.length) {
        setSlotError('Could not save slots — they may overlap existing ones.');
        setSaving(false);
        return;
      }
      if (failed.length > 0) {
        setSlotError(`Saved ${results.length - failed.length} of ${results.length} days. Some overlapped existing slots.`);
      }
      await fetchData();
      setShowSlotModal(false);
      setSelectedDays([1]);
    }
    catch (err) { setSlotError(err.response?.data?.error || 'Failed to save slot.'); }
    finally { setSaving(false); }
  };

  const deleteSlot = async id => { try { await axios.delete(`/api/instructor/availability/slots/${id}`); await fetchData(); } catch (err) { console.error(err); } };

  const saveException = async e => {
    e.preventDefault(); setSaving(true); setExError('');
    try { await axios.post('/api/instructor/availability/exceptions', exForm); await fetchData(); setShowExceptionModal(false); setExForm({ exception_date:'', start_time:'', end_time:'', reason:'' }); }
    catch (err) { setExError(err.response?.data?.error || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const deleteException = async id => { try { await axios.delete(`/api/instructor/availability/exceptions/${id}`); await fetchData(); } catch (err) { console.error(err); } };
  const toggleAccepting = async () => { try { await axios.patch('/api/instructor/profile', { is_accepting_bookings: !profile?.is_accepting_bookings }); await fetchData(); } catch (err) { console.error(err); } };
  const updateDuration = async val => { try { await axios.patch('/api/instructor/profile', { session_duration_minutes: parseInt(val) }); await fetchData(); } catch (err) { console.error(err); } };

  const slotsByDay = DAYS.map((day, i) => ({ day, slots: slots.filter(s => s.day_of_week === i) }));

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">AVAILABILITY</h1><p className="page-subtitle">Set your weekly schedule and block off dates</p></div>

      <div style={{ display:'flex', gap:'16px', marginBottom:'32px', flexWrap:'wrap' }}>
        <div className="card" style={{ flex:'1', minWidth:'200px' }}>
          <div className="stat-card-label" style={{ marginBottom:'12px' }}>Session Duration</div>
          <select className="form-input" value={profile?.session_duration_minutes||60} onChange={e => updateDuration(e.target.value)}>
            {[30,45,60,90,120].map(d => <option key={d} value={d}>{d} minutes</option>)}
          </select>
        </div>
        <div className="card" style={{ flex:'1', minWidth:'200px' }}>
          <div className="stat-card-label" style={{ marginBottom:'12px' }}>Accepting Bookings</div>
          <button className={`btn btn-sm ${profile?.is_accepting_bookings?'btn-outline':'btn-primary'}`} onClick={toggleAccepting}>
            {profile?.is_accepting_bookings ? '✓ Currently Accepting — Click to Pause' : '✗ Paused — Click to Accept'}
          </button>
        </div>
      </div>

      <div className="table-container" style={{ marginBottom:'24px' }}>
        <div className="table-toolbar">
          <span className="table-toolbar-title">Weekly Schedule</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowSlotModal(true)}>+ Add Time Slot</button>
        </div>
        <div style={{ padding:'16px 20px' }}>
          {slotsByDay.every(d => d.slots.length === 0) ? (
            <div className="empty-state" style={{ padding:'32px' }}><span className="empty-state-icon">🗓</span><span className="empty-state-text">No availability set yet.</span></div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
              {slotsByDay.map(({ day, slots: daySlots }, i) => daySlots.length > 0 && (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'16px', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ width:'90px', fontSize:'0.8rem', fontWeight:'600', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{day}</span>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', flex:1 }}>
                    {daySlots.map(slot => (
                      <div key={slot.id} style={{ display:'flex', alignItems:'center', gap:'8px', background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'6px 12px' }}>
                        <span style={{ fontSize:'0.875rem', color:'var(--text-primary)' }}>{formatTime(slot.start_time)} – {formatTime(slot.end_time)}</span>
                        <button onClick={() => deleteSlot(slot.id)} style={{ color:'var(--text-muted)', fontSize:'0.75rem', cursor:'pointer' }}>✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <span className="table-toolbar-title">Blocked Dates</span>
          <button className="btn btn-outline btn-sm" onClick={() => setShowExceptionModal(true)}>+ Block a Date</button>
        </div>
        {exceptions.length === 0 ? (
          <div className="empty-state"><span className="empty-state-icon">📆</span><span className="empty-state-text">No blocked dates</span></div>
        ) : (
          <table>
            <thead><tr><th>Date</th><th>Time Range</th><th>Reason</th><th></th></tr></thead>
            <tbody>
              {exceptions.map(ex => (
                <tr key={ex.id}>
                  <td className="td-primary">{formatDate(ex.exception_date)}</td>
                  <td>{ex.start_time ? `${formatTime(ex.start_time)} – ${formatTime(ex.end_time)}` : 'All day'}</td>
                  <td>{ex.reason || '—'}</td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => deleteException(ex.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showSlotModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowSlotModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">ADD TIME SLOT</span><button className="modal-close" onClick={() => setShowSlotModal(false)}>✕</button></div>
            <form onSubmit={saveSlot}>
              <div className="modal-body">
                {slotError && <div className="auth-error-banner"><span>⚠</span> {slotError}</div>}
                <div className="form-group">
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                    <label className="form-label" style={{ margin:0 }}>Days</label>
                    <button type="button"
                      onClick={toggleAllDays}
                      style={{ fontSize:'0.75rem', color: selectedDays.length === 7 ? '#4ade80' : 'var(--text-muted)', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-body)', fontWeight:'600', letterSpacing:'0.05em' }}>
                      {selectedDays.length === 7 ? '✓ All Week Selected' : 'Select All Week'}
                    </button>
                  </div>
                  <div className="day-grid">
                    {DAYS_SHORT.map((d,i) => (
                      <button key={i} type="button"
                        className={`day-pill ${selectedDays.includes(i) ? 'selected' : ''}`}
                        onClick={() => toggleDay(i)}>
                        {d}
                      </button>
                    ))}
                  </div>
                  {selectedDays.length > 1 && (
                    <p style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:'8px' }}>
                      {selectedDays.length === 7 ? 'Applying to all 7 days' : `Applying to ${selectedDays.length} selected days`}
                    </p>
                  )}
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  <div className="form-group"><label className="form-label">Start Time</label><input type="time" className="form-input" value={slotForm.start_time} onChange={e => setSlotForm(f => ({...f, start_time:e.target.value}))} required /></div>
                  <div className="form-group"><label className="form-label">End Time</label><input type="time" className="form-input" value={slotForm.end_time} onChange={e => setSlotForm(f => ({...f, end_time:e.target.value}))} required /></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowSlotModal(false); setSelectedDays([1]); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving?'Saving...':'Add Slot'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExceptionModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowExceptionModal(false)}>
          <div className="modal">
            <div className="modal-header"><span className="modal-title">BLOCK A DATE</span><button className="modal-close" onClick={() => setShowExceptionModal(false)}>✕</button></div>
            <form onSubmit={saveException}>
              <div className="modal-body">
                {exError && <div className="auth-error-banner"><span>⚠</span> {exError}</div>}
                <div className="form-group"><label className="form-label">Date</label><input type="date" className="form-input" value={exForm.exception_date} onChange={e => setExForm(f => ({...f, exception_date:e.target.value}))} required /></div>
                <p style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>Leave times blank to block the entire day.</p>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                  <div className="form-group"><label className="form-label">Start Time (optional)</label><input type="time" className="form-input" value={exForm.start_time} onChange={e => setExForm(f => ({...f, start_time:e.target.value}))} /></div>
                  <div className="form-group"><label className="form-label">End Time (optional)</label><input type="time" className="form-input" value={exForm.end_time} onChange={e => setExForm(f => ({...f, end_time:e.target.value}))} /></div>
                </div>
                <div className="form-group"><label className="form-label">Reason (optional)</label><input type="text" className="form-input" placeholder="e.g. Vacation, Holiday" value={exForm.reason} onChange={e => setExForm(f => ({...f, reason:e.target.value}))} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowExceptionModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving?'Saving...':'Block Date'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Sessions ----
function InstructorSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [pending, setPending] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [detailSession, setDetailSession] = useState(null);

  const [rescheduleSession, setRescheduleSession] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ session_date: '', start_time: '', end_time: '', duration_minutes: 60 });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');

  const [reassignSession, setReassignSession] = useState(null);
  const [reassignInstructors, setReassignInstructors] = useState([]);
  const [reassignId, setReassignId] = useState('');
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignError, setReassignError] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const openReassign = async (session) => {
    setReassignError(''); setReassignId('');
    try {
      const r = await axios.get('/api/instructor/instructors');
      setReassignInstructors(r.data.instructors);
    } catch { setReassignInstructors([]); }
    setReassignSession(session);
  };

  const submitReassign = async () => {
    if (!reassignId) { setReassignError('Please select an instructor.'); return; }
    setReassignLoading(true); setReassignError('');
    try {
      await axios.patch(`/api/instructor/sessions/${reassignSession.id}/reassign`, { instructor_id: reassignId });
      setReassignSession(null);
      fetchSessions();
    } catch (err) {
      setReassignError(err.response?.data?.error || 'Failed to reassign. Please try again.');
    } finally { setReassignLoading(false); }
  };

  const openReschedule = (session) => {
    const dateStr = session.session_date ? session.session_date.split('T')[0] : '';
    setRescheduleForm({ session_date: dateStr, start_time: session.start_time || '', end_time: session.end_time || '', duration_minutes: session.duration_minutes || 60 });
    setRescheduleError('');
    setRescheduleSession(session);
  };

  const submitReschedule = async () => {
    if (!rescheduleForm.session_date || !rescheduleForm.start_time || !rescheduleForm.end_time) {
      setRescheduleError('Please fill in all fields.'); return;
    }
    if (rescheduleForm.start_time >= rescheduleForm.end_time) {
      setRescheduleError('End time must be after start time.'); return;
    }
    setRescheduleLoading(true); setRescheduleError('');
    try {
      await axios.patch(`/api/instructor/sessions/${rescheduleSession.id}/reschedule`, rescheduleForm);  // includes duration_minutes
      setRescheduleSession(null);
      fetchSessions();
    } catch (err) {
      setRescheduleError(err.response?.data?.error || 'Failed to reschedule. Please try again.');
    } finally { setRescheduleLoading(false); }
  };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    try { const r = await axios.get('/api/instructor/sessions', { params }); setSessions(r.data.sessions); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const filtered = sessions.filter(s => {
    if (mineOnly && s.instructor_id !== user?.id) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return `${s.parent_first} ${s.parent_last}`.toLowerCase().includes(q) ||
      (s.player_first && `${s.player_first} ${s.player_last}`.toLowerCase().includes(q)) ||
      `${s.instructor_first} ${s.instructor_last}`.toLowerCase().includes(q);
  });

  useEffect(() => { setPage(1); }, [search, statusFilter, mineOnly]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const confirmAction = async (reason) => {
    setUpdating(true);
    try {
      await axios.patch(`/api/instructor/sessions/${pending.session.id}/status`, { status: pending.action.status, reason: reason || undefined });
      setPending(null);
      await fetchSessions();
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  const exportCSV = () => {
    const headers = ['Date','Time','Instructor','Parent','Player','Status','Notes'];
    const rows = filtered.map(s => [
      formatDate(s.session_date),
      `${formatTime(s.start_time)} - ${formatTime(s.end_time)}`,
      `${s.instructor_first} ${s.instructor_last}`,
      `${s.parent_first} ${s.parent_last}`,
      s.player_first ? `${s.player_first} ${s.player_last}` : '',
      s.status,
      (s.notes || '').replace(/,/g, ';'),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'my-sessions.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">ALL SESSIONS</h1><p className="page-subtitle">All training sessions — reassign yours to another instructor</p></div>
      <div className="table-container">
        <div className="table-toolbar">
          <span className="table-toolbar-title">Sessions</span>
          <input className="table-search" placeholder="Search by instructor, parent or player..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="table-search" style={{ width:'160px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>
          <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'0.85rem', color:'var(--text-secondary)', cursor:'pointer', whiteSpace:'nowrap' }}>
            <input type="checkbox" checked={mineOnly} onChange={e => setMineOnly(e.target.checked)} style={{ width:'15px', height:'15px', cursor:'pointer' }} />
            Mine only
          </label>
          <select className="table-search" style={{ width:'130px' }} value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={exportCSV}>↓ CSV</button>
        </div>

        {loading ? <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
        : filtered.length === 0 ? <div className="empty-state"><span className="empty-state-icon">📅</span><span className="empty-state-text">No sessions found</span></div>
        : (
          <table>
            <thead><tr><th>Date</th><th>Time</th><th>Instructor</th><th>Parent</th><th>Player</th><th>Status</th><th>Notes</th><th>Actions</th></tr></thead>
            <tbody>
              {paginated.map(s => {
                const isOwner = s.instructor_id === user?.id;
                return (
                <tr key={s.id}>
                  <td className="td-primary" style={{ cursor:'pointer' }} onClick={() => setDetailSession(s)}>{formatDate(s.session_date)}</td>
                  <td>{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                  <td style={{ color: isOwner ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: isOwner ? '600' : 'normal' }}>
                    {s.instructor_first} {s.instructor_last}{isOwner && <span style={{ fontSize:'0.65rem', color:'#60a5fa', marginLeft:'5px' }}>me</span>}
                  </td>
                  <td>{s.parent_first} {s.parent_last}</td>
                  <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                  <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                  <td>
                    {s.notes ? (
                      <span style={{ fontSize:'0.8rem', color:'var(--text-secondary)', cursor:'pointer', maxWidth:'120px', display:'inline-block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} onClick={() => setDetailSession(s)} title={s.notes}>{s.notes}</span>
                    ) : isOwner ? (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem' }} onClick={() => setDetailSession(s)}>+ Add</button>
                    ) : '—'}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                      <button className="badge badge-default" style={{ cursor:'pointer', border:'none' }} onClick={() => setDetailSession(s)}>View</button>
                      {isOwner && (INSTR_STATUS_ACTIONS[s.status]||[]).map(action => (
                        <button key={action.status} className={`badge ${action.style}`}
                          style={{ cursor:'pointer', border:'none', padding:'4px 10px' }}
                          onClick={() => setPending({ session:s, action })}>
                          {action.label}
                        </button>
                      ))}
                      {isOwner && ['pending','confirmed'].includes(s.status) && (
                        <button className="badge badge-default"
                          style={{ cursor:'pointer', border:'none', padding:'4px 10px', background:'rgba(96,165,250,0.15)', color:'#60a5fa' }}
                          onClick={() => openReschedule(s)}>
                          Reschedule
                        </button>
                      )}
                      {isOwner && ['pending','confirmed'].includes(s.status) && (
                        <button className="badge badge-default"
                          style={{ cursor:'pointer', border:'none', padding:'4px 10px', background:'rgba(251,146,60,0.15)', color:'#fb923c' }}
                          onClick={() => openReassign(s)}>
                          Reassign
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderTop:'1px solid var(--border)', flexWrap:'wrap', gap:'12px' }}>
            <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
              Showing {Math.min((page-1)*perPage+1, filtered.length)}–{Math.min(page*perPage, filtered.length)} of {filtered.length}
            </span>
            {totalPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>← Prev</button>
                <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)', minWidth:'90px', textAlign:'center' }}>Page {page} of {totalPages}</span>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>

      {pending && <ConfirmModal action={pending.action} session={pending.session} onConfirm={confirmAction} onClose={() => setPending(null)} updating={updating} />}
      {detailSession && <SessionDetailModal session={detailSession} onClose={() => setDetailSession(null)} onUpdated={fetchSessions} />}
      {reassignSession && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setReassignSession(null)}>
          <div className="modal" style={{ maxWidth:'420px' }}>
            <div className="modal-header">
              <span className="modal-title">REASSIGN SESSION</span>
              <button className="modal-close" onClick={() => setReassignSession(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:'16px' }}>
                Reassigning session with <b style={{ color:'var(--text-primary)' }}>{reassignSession.parent_first} {reassignSession.parent_last}</b> on <b style={{ color:'var(--text-primary)' }}>{formatDate(reassignSession.session_date)}</b>. The parent will be notified by email.
              </p>
              {reassignError && <div className="auth-error-banner" style={{ marginBottom:'12px' }}>⚠ {reassignError}</div>}
              <div className="form-group">
                <label className="form-label">New Instructor</label>
                {reassignInstructors.length === 0 ? (
                  <p style={{ fontSize:'0.85rem', color:'var(--text-muted)' }}>No other active instructors available.</p>
                ) : (
                  <select className="form-input" value={reassignId} onChange={e => setReassignId(e.target.value)}>
                    <option value="">— Select instructor —</option>
                    {reassignInstructors.map(i => (
                      <option key={i.id} value={i.id}>{i.first_name} {i.last_name}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setReassignSession(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={submitReassign} disabled={reassignLoading || reassignInstructors.length === 0}>
                {reassignLoading ? 'Reassigning...' : 'Reassign Session'}
              </button>
            </div>
          </div>
        </div>
      )}
      {rescheduleSession && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setRescheduleSession(null)}>
          <div className="modal" style={{ maxWidth:'460px' }}>
            <div className="modal-header">
              <span className="modal-title">RESCHEDULE SESSION</span>
              <button className="modal-close" onClick={() => setRescheduleSession(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:'16px' }}>
                Rescheduling session with <b style={{color:'var(--text-primary)'}}>{rescheduleSession.parent_first} {rescheduleSession.parent_last}</b>.
                The parent will be notified of the new date and time.
              </p>
              {rescheduleError && (
                <div className="auth-error-banner" style={{ marginBottom:'12px' }}>⚠ {rescheduleError}</div>
              )}
              <div className="form-group">
                <label className="form-label">New Date</label>
                <input type="date" className="form-input"
                  value={rescheduleForm.session_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setRescheduleForm(f => ({ ...f, session_date: e.target.value }))} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div className="form-group">
                  <label className="form-label">New Start Time</label>
                  <input type="time" className="form-input"
                    value={rescheduleForm.start_time}
                    onChange={e => setRescheduleForm(f => ({ ...f, start_time: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">New End Time</label>
                  <input type="time" className="form-input"
                    value={rescheduleForm.end_time}
                    onChange={e => setRescheduleForm(f => ({ ...f, end_time: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Duration</label>
                <select className="form-input" value={rescheduleForm.duration_minutes}
                  onChange={e => setRescheduleForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}>
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
              <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'12px 14px', marginTop:'8px' }}>
                <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600' }}>Current Schedule</p>
                <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)', margin:'0' }}>
                  {rescheduleSession.session_date ? rescheduleSession.session_date.split('T')[0] : ''} at {rescheduleSession.start_time} — {rescheduleSession.end_time} ({rescheduleSession.duration_minutes} min)
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setRescheduleSession(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={submitReschedule} disabled={rescheduleLoading}>
                {rescheduleLoading ? 'Saving...' : 'Save New Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Instructor Support ----
const TICKET_STATUS_INSTR = {
  open:        { bg:'rgba(251,191,36,0.15)', color:'#fbbf24', label:'Open' },
  in_progress: { bg:'rgba(96,165,250,0.15)', color:'#60a5fa', label:'In Progress' },
  resolved:    { bg:'rgba(74,222,128,0.15)', color:'#4ade80', label:'Resolved' },
};
const fmtDT = ts => { if (!ts) return '—'; const d=new Date(ts); return d.toLocaleDateString('en-US',{month:'short',day:'numeric'})+' '+d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'}); };

function InstructorSupportTicketModal({ ticket, onClose, onUpdated }) {
  const [messages, setMessages] = useState([]);
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const fetchDetail = useCallback(async () => {
    try {
      const r = await axios.get(`/api/support/tickets/${ticket.id}`);
      setMessages(r.data.messages);
      setCurrentTicket(r.data.ticket);
      await axios.patch('/api/support/notifications/read', { ticket_id: ticket.id });
    } catch (err) { console.error(err); }
    finally { setLoadingMsgs(false); }
  }, [ticket.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/support/tickets/${ticket.id}/messages`, { message: reply });
      setReply('');
      await fetchDetail();
      if (onUpdated) onUpdated();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const st = TICKET_STATUS_INSTR[currentTicket.status] || { bg:'var(--bg-elevated)', color:'var(--text-muted)', label:currentTicket.status };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:'580px', display:'flex', flexDirection:'column', maxHeight:'85vh' }}>
        <div className="modal-header" style={{ gap:'12px' }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div className="modal-title" style={{ fontSize:'1.05rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'6px' }}>{currentTicket.subject}</div>
            <span style={{ background:st.bg, color:st.color, padding:'3px 10px', borderRadius:'20px', fontSize:'0.75rem', fontWeight:'600' }}>{st.label}</span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 24px', display:'flex', flexDirection:'column', gap:'12px' }}>
          {loadingMsgs ? <p style={{ color:'var(--text-muted)', fontSize:'0.875rem' }}>Loading...</p> : messages.map(msg => {
            const isAdmin = msg.role === 'admin';
            return (
              <div key={msg.id} style={{ display:'flex', flexDirection:'column', alignItems: isAdmin ? 'flex-start' : 'flex-end' }}>
                <div style={{
                  maxWidth:'80%',
                  background: isAdmin ? 'rgba(96,165,250,0.1)' : 'var(--bg-elevated)',
                  border:`1px solid ${isAdmin ? 'rgba(96,165,250,0.25)' : 'var(--border)'}`,
                  borderRadius: isAdmin ? '12px 12px 12px 4px' : '12px 12px 4px 12px',
                  padding:'10px 14px',
                }}>
                  <div style={{ fontSize:'0.78rem', fontWeight:'600', color: isAdmin ? '#60a5fa' : 'var(--text-primary)', marginBottom:'4px' }}>
                    {isAdmin ? 'Support Team' : 'You'}
                  </div>
                  <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)', lineHeight:'1.5', whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{msg.message}</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'6px', textAlign: isAdmin ? 'left' : 'right' }}>{fmtDT(msg.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
        {currentTicket.status !== 'resolved' ? (
          <div style={{ padding:'12px 24px 16px', borderTop:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:'8px' }}>
            <textarea className="form-input" rows={3} placeholder="Write a reply..." value={reply} onChange={e => setReply(e.target.value)} style={{ resize:'vertical', fontSize:'0.875rem' }}
              onKeyDown={e => { if (e.key==='Enter' && (e.metaKey||e.ctrlKey)) handleSend(); }} />
            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="btn btn-sm" onClick={handleSend} disabled={sending || !reply.trim()} style={{ opacity:(!reply.trim()||sending)?0.5:1 }}>
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding:'12px 24px 16px', borderTop:'1px solid var(--border)', textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>
            This ticket has been resolved. Open a new ticket if you need further help.
          </div>
        )}
      </div>
    </div>
  );
}

function InstructorSupport({ onNotifRead }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [newError, setNewError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const r = await axios.get('/api/support/tickets/my');
      setTickets(r.data.tickets);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchTickets();
    axios.patch('/api/support/notifications/read').catch(() => {});
    if (onNotifRead) onNotifRead();
  }, [fetchTickets, onNotifRead]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newForm.subject.trim() || !newForm.message.trim()) { setNewError('Subject and message are required.'); return; }
    setSubmitting(true); setNewError('');
    try {
      await axios.post('/api/support/tickets', newForm);
      setNewForm({ subject:'', message:'' });
      setShowNew(false);
      await fetchTickets();
    } catch (err) { setNewError(err.response?.data?.error || 'Failed to submit ticket.'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
        <div>
          <h1 className="page-title">SUPPORT</h1>
          <p className="page-subtitle">Get help from our team. We'll respond as soon as possible.</p>
        </div>
        {!showNew && (
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Ticket</button>
        )}
      </div>

      {showNew && (
        <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'24px', marginBottom:'24px' }}>
          <div style={{ fontWeight:'600', fontSize:'1rem', color:'var(--text-primary)', marginBottom:'16px' }}>Open a Support Ticket</div>
          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
            <div className="form-group">
              <label className="form-label">Subject</label>
              <input className="form-input" value={newForm.subject} onChange={e => setNewForm(f=>({...f,subject:e.target.value}))} placeholder="Brief description of your issue" maxLength={255} />
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea className="form-input" rows={4} value={newForm.message} onChange={e => setNewForm(f=>({...f,message:e.target.value}))} placeholder="Please describe your issue in detail..." style={{ resize:'vertical' }} />
            </div>
            {newError && <div className="auth-error-banner"><span>⚠</span> {newError}</div>}
            <div style={{ display:'flex', gap:'10px' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={submitting}>{submitting ? 'Submitting...' : 'Submit Ticket'}</button>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setShowNew(false); setNewError(''); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <p style={{ color:'var(--text-muted)' }}>Loading tickets...</p> : tickets.length === 0 && !showNew ? (
        <div className="empty-state">
          <div className="empty-state-icon">🎫</div>
          <p className="empty-state-text">No support tickets yet</p>
          <button className="btn btn-primary btn-sm" style={{ marginTop:'8px' }} onClick={() => setShowNew(true)}>Open a Ticket</button>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
          {tickets.map(t => {
            const st = TICKET_STATUS_INSTR[t.status] || { bg:'var(--bg-elevated)', color:'var(--text-muted)', label:t.status };
            return (
              <div key={t.id} onClick={() => setSelectedTicket(t)} style={{
                background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)',
                padding:'16px 20px', cursor:'pointer', transition:'border-color 0.15s, background 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor='var(--border-light)'; e.currentTarget.style.background='var(--bg-elevated)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.background='var(--bg-card)'; }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'12px' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:'600', color:'var(--text-primary)', fontSize:'0.95rem', marginBottom:'4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.subject}</div>
                    <div style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
                      #{t.id} · {t.message_count} {parseInt(t.message_count)===1?'message':'messages'} · Last activity {fmtDT(t.last_message_at || t.updated_at)}
                    </div>
                  </div>
                  <span style={{ background:st.bg, color:st.color, padding:'3px 10px', borderRadius:'20px', fontSize:'0.72rem', fontWeight:'600', whiteSpace:'nowrap', flexShrink:0 }}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedTicket && (
        <InstructorSupportTicketModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdated={() => { setSelectedTicket(null); fetchTickets(); if (onNotifRead) onNotifRead(); }} />
      )}
    </div>
  );
}

export default function InstructorDashboard() {
  const [supportBadge, setSupportBadge] = useState(0);

  const fetchSupportBadge = useCallback(() => {
    axios.get('/api/support/notifications').then(r => setSupportBadge(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => { fetchSupportBadge(); }, [fetchSupportBadge]);

  const navItems = useMemo(() => BASE_NAV.map(item =>
    item.path === '/instructor/support' ? { ...item, badge: supportBadge } : item
  ), [supportBadge]);

  return (
    <Layout navItems={navItems} helpUrl="/help/instructor">
      <Routes>
        <Route index element={<InstructorHome supportBadge={supportBadge} />} />
        <Route path="profile" element={<InstructorProfile />} />
        <Route path="availability" element={<InstructorAvailability />} />
        <Route path="sessions" element={<InstructorSessions />} />
        <Route path="calendar" element={<InstructorCalendar />} />
        <Route path="support" element={<InstructorSupport onNotifRead={fetchSupportBadge} />} />
        <Route path="*" element={<Navigate to="/instructor" replace />} />
      </Routes>
    </Layout>
  );
}

